import { useMemo, useCallback, useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wand2, AlertTriangle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ProjectAvailability,
  ProjectFunctionSeries,
} from "@/hooks/useProjectPeople";
import { MealPlan, HotelPlan } from "@/hooks/useProjectPlanning";
import { cn } from "@/lib/utils";
import {
  getPersonInitials,
  useHorizontalScrollState,
} from "./pinnedColumnUtils";

interface MealHotelPlanningTableProps {
  availability: ProjectAvailability[];
  functions: ProjectFunctionSeries[];
  projectDays: Date[];
  mealPlans: MealPlan[];
  hotelPlans: HotelPlan[];
  onUpdateMeal: (
    personId: string,
    date: string,
    field: "lunch" | "dinner",
    value: boolean,
  ) => void;
  onUpdateHotel: (
    personId: string,
    date: string,
    value: boolean,
    roomType?: "single" | "double",
    roommatePersonId?: string | null,
    syncRoommatePair?: boolean,
  ) => void;
  onInitFromFunctions: (personId?: string) => void;
  isUpdating?: boolean;
}

export function MealHotelPlanningTable({
  availability,
  functions,
  projectDays,
  mealPlans,
  hotelPlans,
  onUpdateMeal,
  onUpdateHotel,
  onInitFromFunctions,
  isUpdating,
}: MealHotelPlanningTableProps) {
  const { scrollRef, isScrolledX, handleScroll } = useHorizontalScrollState();
  const [confirmInit, setConfirmInit] = useState<{
    open: boolean;
    personId?: string;
    personName?: string;
  }>({ open: false });

  // Only show booked or available people
  const activePeople = useMemo(() => {
    return availability.filter(
      (a) => a.status === "available" || a.status === "booked",
    );
  }, [availability]);

  // Flatten series into days for assignment lookup
  const allFunctionDays = useMemo(
    () => functions.flatMap((s) => s.days),
    [functions],
  );

  // Index meal plans by person_id + date
  const mealPlanMap = useMemo(() => {
    const map = new Map<string, MealPlan>();
    mealPlans.forEach((mp) => {
      map.set(`${mp.person_id}_${mp.plan_date}`, mp);
    });
    return map;
  }, [mealPlans]);

  // Index hotel plans by person_id + date
  const hotelPlanMap = useMemo(() => {
    const map = new Map<string, HotelPlan>();
    hotelPlans.forEach((hp) => {
      map.set(`${hp.person_id}_${hp.plan_date}`, hp);
    });
    return map;
  }, [hotelPlans]);

  // Check if a person has a function assignment on a given date
  const hasAssignment = useCallback(
    (personId: string, dateStr: string) => {
      return allFunctionDays.some(
        (fn) =>
          fn.function_date === dateStr &&
          fn.assignments?.some((a) => a.person_id === personId),
      );
    },
    [allFunctionDays],
  );

  const getPersonAllergyTags = useCallback((avail: ProjectAvailability) => {
    return (
      avail.person?.person_allergy_tags?.map((pat) => pat.allergy_tag) ?? []
    );
  }, []);

  const getMealPlan = useCallback(
    (personId: string, dateStr: string) => {
      return mealPlanMap.get(`${personId}_${dateStr}`);
    },
    [mealPlanMap],
  );

  const getHotelPlan = useCallback(
    (personId: string, dateStr: string) => {
      return hotelPlanMap.get(`${personId}_${dateStr}`);
    },
    [hotelPlanMap],
  );

  // Check if a person has any existing meal/hotel plans
  const hasExistingPlans = useCallback(
    (personId?: string) => {
      if (personId) {
        return (
          mealPlans.some(
            (mp) => mp.person_id === personId && (mp.lunch || mp.dinner),
          ) ||
          hotelPlans.some((hp) => hp.person_id === personId && hp.needs_hotel)
        );
      }
      return (
        mealPlans.some((mp) => mp.lunch || mp.dinner) ||
        hotelPlans.some((hp) => hp.needs_hotel)
      );
    },
    [mealPlans, hotelPlans],
  );

  const handleInitClick = (personId?: string, personName?: string) => {
    if (hasExistingPlans(personId)) {
      setConfirmInit({ open: true, personId, personName });
    } else {
      onInitFromFunctions(personId);
    }
  };

  const handleConfirmInit = () => {
    onInitFromFunctions(confirmInit.personId);
    setConfirmInit({ open: false });
  };

  // Count totals per day
  const dayCounts = useMemo(() => {
    const counts: Record<
      string,
      {
        lunch: number;
        dinner: number;
        single: number;
        double: number;
      }
    > = {};
    projectDays.forEach((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      let lunch = 0,
        dinner = 0,
        single = 0;
      const doubleRoomKeys = new Set<string>();
      activePeople.forEach((avail) => {
        const meal = mealPlanMap.get(`${avail.person_id}_${dateStr}`);
        const hotelP = hotelPlanMap.get(`${avail.person_id}_${dateStr}`);
        if (meal?.lunch) lunch++;
        if (meal?.dinner) dinner++;
        if (hotelP?.needs_hotel) {
          if (hotelP.room_type === "double" && hotelP.roommate_person_id) {
            doubleRoomKeys.add(
              [avail.person_id, hotelP.roommate_person_id].sort().join("_"),
            );
          } else {
            single++;
          }
        }
      });
      counts[dateStr] = {
        lunch,
        dinner,
        single,
        double: doubleRoomKeys.size,
      };
    });
    return counts;
  }, [projectDays, activePeople, mealPlanMap, hotelPlanMap]);

  if (activePeople.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No people with "Available" or "Booked" status.</p>
        <p className="text-sm">Update availability status first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Check meals and hotel nights for each person per day. Use "Init from
          Functions" to auto-fill based on function assignments.
        </p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleInitClick()}
                disabled={isUpdating}
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Init from Functions
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Set lunch for all assigned days; dinner + hotel on nights
                between consecutive working days
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative w-full min-w-0 max-w-full overflow-x-auto"
      >
        <table className="min-w-max caption-bottom text-sm">
          <TableHeader>
            <TableRow>
              <TableHead
                className={cn(
                  "sticky left-0 z-10 bg-background transition-[width,min-width] duration-150",
                  isScrolledX
                    ? "w-14 min-w-14 max-w-14 text-center"
                    : "min-w-[180px]",
                )}
                title="Person"
              >
                {isScrolledX ? "P" : "Person"}
              </TableHead>
              {projectDays.map((day) => (
                <TableHead
                  key={day.toISOString()}
                  className="text-center min-w-[140px]"
                >
                  <div className="text-xs">
                    <div>{format(day, "EEE")}</div>
                    <div className="font-normal">{format(day, "dd.MM")}</div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {activePeople.map((avail) => {
              const allergyTags = getPersonAllergyTags(avail);
              const personName = `${avail.person?.first_name} ${avail.person?.last_name}`;
              return (
                <TableRow key={avail.id}>
                  <TableCell
                    className={cn(
                      "sticky left-0 z-10 bg-background font-medium transition-[width,min-width] duration-150",
                      isScrolledX
                        ? "w-14 min-w-14 max-w-14 text-center"
                        : "min-w-[180px]",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-start justify-between gap-1",
                        isScrolledX && "justify-center",
                      )}
                    >
                      <div className="min-w-0">
                        <p
                          className={cn(
                            "text-sm",
                            isScrolledX
                              ? "inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold"
                              : "",
                          )}
                          title={personName}
                        >
                          {isScrolledX
                            ? getPersonInitials(
                                avail.person?.first_name,
                                avail.person?.last_name,
                              )
                            : `${avail.person?.first_name} ${avail.person?.last_name}`}
                        </p>
                        {!isScrolledX &&
                          (allergyTags.length > 0 ||
                            avail.person?.allergies_notes) && (
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                              {allergyTags.map((tag) => (
                                <Badge
                                  key={tag.id}
                                  variant="outline"
                                  className="h-4 px-1 text-[10px] text-amber-600 border-amber-300"
                                >
                                  {tag.name}
                                </Badge>
                              ))}
                              {avail.person?.allergies_notes && (
                                <span className="text-[10px] text-amber-600">
                                  {avail.person.allergies_notes}
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                      {!isScrolledX && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() =>
                                  handleInitClick(avail.person_id, personName)
                                }
                                disabled={isUpdating}
                              >
                                <Wand2 className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Init from functions for this person</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  {projectDays.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const meal = getMealPlan(avail.person_id, dateStr);
                    const hotel = getHotelPlan(avail.person_id, dateStr);
                    const assigned = hasAssignment(avail.person_id, dateStr);
                    const roommateOptions = activePeople.filter(
                      (person) => person.person_id !== avail.person_id,
                    );
                    const selectedRoommateId =
                      hotel?.roommate_person_id ?? null;

                    return (
                      <TableCell
                        key={day.toISOString()}
                        className={cn(
                          "p-1.5 align-top",
                          assigned && "bg-primary/5",
                        )}
                      >
                        <div className="space-y-0.5 text-xs">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={meal?.lunch ?? false}
                              onCheckedChange={(v) =>
                                onUpdateMeal(
                                  avail.person_id,
                                  dateStr,
                                  "lunch",
                                  !!v,
                                )
                              }
                            />
                            <span>L</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={meal?.dinner ?? false}
                              onCheckedChange={(v) =>
                                onUpdateMeal(
                                  avail.person_id,
                                  dateStr,
                                  "dinner",
                                  !!v,
                                )
                              }
                            />
                            <span>D</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={hotel?.needs_hotel ?? false}
                              onCheckedChange={(v) =>
                                onUpdateHotel(
                                  avail.person_id,
                                  dateStr,
                                  !!v,
                                  hotel?.needs_hotel
                                    ? hotel.room_type
                                    : undefined,
                                  hotel?.needs_hotel
                                    ? hotel.roommate_person_id
                                    : undefined,
                                )
                              }
                            />
                            <span>H</span>
                          </label>
                          {hotel?.needs_hotel && (
                            <div className="space-y-1 pt-1">
                              <Select
                                value={hotel.room_type}
                                onValueChange={(value) =>
                                  onUpdateHotel(
                                    avail.person_id,
                                    dateStr,
                                    true,
                                    value as "single" | "double",
                                    null,
                                    value === "single" &&
                                      Boolean(hotel.roommate_person_id),
                                  )
                                }
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="single">Single</SelectItem>
                                  <SelectItem
                                    value="double"
                                    disabled={roommateOptions.length === 0}
                                  >
                                    Double
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {hotel.room_type === "double" && (
                                <Select
                                  value={selectedRoommateId ?? "__none__"}
                                  onValueChange={(value) =>
                                    onUpdateHotel(
                                      avail.person_id,
                                      dateStr,
                                      true,
                                      "double",
                                      value === "__none__" ? null : value,
                                      true,
                                    )
                                  }
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder="Roommate" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">
                                      No roommate
                                    </SelectItem>
                                    {roommateOptions.map((person) => (
                                      <SelectItem
                                        key={person.person_id}
                                        value={person.person_id}
                                      >
                                        {person.person?.first_name}{" "}
                                        {person.person?.last_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {/* Totals row */}
            <TableRow className="border-t-2 font-medium">
              <TableCell
                className={cn(
                  "sticky left-0 z-10 bg-background text-sm transition-[width,min-width] duration-150",
                  isScrolledX
                    ? "w-14 min-w-14 max-w-14 text-center"
                    : "min-w-[180px]",
                )}
                title="Totals"
              >
                {isScrolledX ? "T" : "Totals"}
              </TableCell>
              {projectDays.map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const counts = dayCounts[dateStr] || {
                  lunch: 0,
                  dinner: 0,
                  single: 0,
                  double: 0,
                };
                return (
                  <TableCell
                    key={day.toISOString()}
                    className="text-center p-1.5"
                  >
                    <div className="space-y-0.5 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="h-4 px-1 text-[10px]"
                        >
                          {counts.lunch}
                        </Badge>
                        <span>L</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="h-4 px-1 text-[10px]"
                        >
                          {counts.dinner}
                        </Badge>
                        <span>D</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="h-4 px-1 text-[10px]"
                        >
                          {counts.single}
                        </Badge>
                        <span>S</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="h-4 px-1 text-[10px]"
                        >
                          {counts.double}
                        </Badge>
                        <span>DBL</span>
                      </div>
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </table>
      </div>

      <AlertDialog
        open={confirmInit.open}
        onOpenChange={(open) => {
          if (!open) setConfirmInit({ open: false });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite existing plans?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmInit.personName
                ? `${confirmInit.personName} already has meal/hotel plans set. This will overwrite them based on function assignments.`
                : "Some people already have meal/hotel plans set. This will overwrite all existing plans based on function assignments."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmInit}>
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
